import { useEffect } from 'react';
import { Action, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import intl from 'react-intl-universal';
import { useParams } from 'react-router-dom';

import LinearProgress from '@mui/material/LinearProgress';

import { Board } from '@/models/BoardModel.ts';

import {
  selectActiveBoard,
  selectIsLoadingActiveBoard,
  selectIsLoadingRoomMembers,
  selectRoomMembersRequested,
} from '../../ducks/Boards';
import { actions as communityActions } from '../../ducks/Communities';
import { selectIsLoadingMilestones, selectMilestonesRequested } from '../../ducks/Milestones';
import { actions as roomActions } from '../../ducks/Rooms';
import { alertAction } from '../../thunks/Alerts';
import * as boardsActions from '../../thunks/Boards';
import * as milestonesActions from '../../thunks/Milestones';

import Error from '../../common/Error';
import { MilestoneCreateContainer as MilestoneCreate, MilestoneUpdateContainer as MilestoneUpdate } from '.';

const mapStateToProps = () =>
  createStructuredSelector({
    board: selectActiveBoard(),
    isLoadingBoard: selectIsLoadingActiveBoard(),
    isLoadingMilestones: selectIsLoadingMilestones(),
    milestonesRequested: selectMilestonesRequested(),
    roomMembersRequested: selectRoomMembersRequested(),
    isLoadingRoomMembers: selectIsLoadingRoomMembers(),
  });

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      ...milestonesActions,
      ...boardsActions,
      setActiveRoom: roomActions.setActiveRoom,
      setActiveCommunity: communityActions.setActiveCommunity,
    },
    dispatch,
  ),
});

const MilestoneContainer = ({
  board,
  isLoadingBoard,
  isLoadingMilestones,
  milestonesRequested,
  roomMembersRequested,
  isLoadingRoomMembers,
  actions,
}: {
  board: Board;
  isLoadingBoard: boolean;
  isLoadingMilestones: boolean;
  milestonesRequested: { boardId: string };
  roomMembersRequested: { communityId: string; roomId: string };
  isLoadingRoomMembers: boolean;
  actions: any;
}) => {
  const params = useParams();
  const { communityId, roomId, boardId, milestoneId } = params;

  useEffect(() => {
    if (!board || board.id !== boardId) {
      actions.fetchActiveBoard({ boardId, communityId, roomId }).then((action: any) => {
        const workflowConfig = action.payload.activeBoard.workflow_config;

        actions.setActiveCommunity({ communityId: workflowConfig.community_id });
        actions.setActiveRoom({ roomId: workflowConfig.room_id });

        alertAction({ action, error: intl.get('card.fetch_board.error') });
      });

      if (milestonesRequested.boardId !== boardId) {
        actions
          .fetchMilestones({ boardId, communityId, roomId })
          .then((action: Action) => alertAction({ action, error: intl.get('milestone.fetch.error') }));
      }
    }

    if (roomMembersRequested.communityId !== communityId || roomMembersRequested.roomId !== roomId) {
      actions.fetchRoomMembers({ communityId, roomId, boardId });
    }
  }, [communityId, roomId, boardId, actions]);

  if (isLoadingBoard) {
    return <LinearProgress />;
  }

  if (!board) {
    return <Error text={intl.get('board.not_found')} />;
  }

  if (
    board?.id !== boardId ||
    isLoadingMilestones ||
    isLoadingRoomMembers ||
    roomMembersRequested.communityId !== communityId ||
    roomMembersRequested.roomId !== roomId
  ) {
    return <LinearProgress />;
  }

  if (milestoneId) {
    return <MilestoneUpdate params={params} />;
  }

  return <MilestoneCreate />;
};

const ConnectedMilestoneContainer = connect(mapStateToProps, mapDispatchToProps)(MilestoneContainer);

export default ConnectedMilestoneContainer;
